import type {MouseEvent as ReactMouseEvent} from 'react';
import {arrayMove} from '@dnd-kit/sortable';
import type {DragEndEvent} from '@dnd-kit/core';
import type {FabricObject} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {LayerType, SELECTION_CHECKBOX_ROLE_SELECTOR, SelectionIntent} from './layers-panel.types';
import type {ApplySelectionOptions, DraggedLayer, LayerEntry, LayerGroupEntry, ReorderOuterRowsParams} from './layers-panel.types';

const GROUP_HEADER_ROW_PREFIX = 'group-';

export function toGroupSortableId(groupId: string): string {
  return `${GROUP_HEADER_ROW_PREFIX}${groupId}`;
}

export function getGroupIdFromSortableId(sortableId: string): string | null {
  if (!sortableId.startsWith(GROUP_HEADER_ROW_PREFIX)) {
    return null;
  }
  return sortableId.slice(GROUP_HEADER_ROW_PREFIX.length);
}

export function getCurrentPage(): Page | undefined {
  return window.posterEditor?.whiteboard?.getCurrentPage();
}

export function findGroupEntry(entries: LayerEntry[], groupId: string): LayerGroupEntry | undefined {
  return entries.find((entry): entry is LayerGroupEntry => entry.layerType === LayerType.Group && entry.groupId === groupId);
}

export function getSortableId(entry: LayerEntry): string {
  if (entry.layerType === LayerType.Item) {
    return entry.uid;
  }
  return toGroupSortableId(entry.groupId);
}

export function getEntryUids(entry: LayerEntry): readonly string[] {
  if (entry.layerType === LayerType.Item) {
    return [entry.uid];
  }
  return entry.memberUids;
}

export function getDraggedLayer(activeId: string, entries: LayerEntry[]): DraggedLayer | null {
  const groupId = getGroupIdFromSortableId(activeId);
  if (groupId !== null) {
    const entry = findGroupEntry(entries, groupId);
    return entry ? {layerType: LayerType.Group, entry} : null;
  }
  const exists = entries.some((e) => (e.layerType === LayerType.Item ? e.uid === activeId : e.memberUids.includes(activeId)));
  return exists ? {layerType: LayerType.Item, uid: activeId} : null;
}

export function areLayerEntriesEqual(a: LayerEntry[], b: LayerEntry[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    const entryA = a[i];
    const entryB = b[i];
    if (entryA.layerType !== entryB.layerType) {
      return false;
    }
    if (entryA.layerType === LayerType.Item && entryB.layerType === LayerType.Item) {
      if (entryA.uid !== entryB.uid) {
        return false;
      }
    } else if (entryA.layerType === LayerType.Group && entryB.layerType === LayerType.Group) {
      if (entryA.groupId !== entryB.groupId || entryA.memberUids.length !== entryB.memberUids.length) {
        return false;
      }
      for (let j = 0; j < entryA.memberUids.length; j += 1) {
        if (entryA.memberUids[j] !== entryB.memberUids[j]) {
          return false;
        }
      }
    }
  }
  return true;
}

/** Applies the selection to the canvas and reports the LAYERS_SELECTED gesture — every caller is a user selection gesture. */
export function applySelection(page: Page, uids: readonly string[], options: ApplySelectionOptions = {}): void {
  trackPosterBuilderGA4Events(GA4EventName.LAYERS_SELECTED);
  const {selectionEvent} = options;
  if (uids.length === 0) {
    page.activeSelection.discardActiveObject();
    return;
  }
  const fabricObjects = uids.map((uid) => page.items.getItem(uid)?.fabricObject).filter((f): f is FabricObject => f !== undefined);
  if (fabricObjects.length === 0) {
    return;
  }
  if (fabricObjects.length === 1) {
    page.activeSelection.setActiveObject(fabricObjects[0], selectionEvent);
    return;
  }
  page.activeSelection.selectFabricObjects(fabricObjects);
}

/** A row's selection checkbox sits inside the clickable row, so the row click handler ignores clicks (and bubbled keyboard activations) that originate from it. */
export function isEventFromSelectionCheckbox(event: ReactMouseEvent): boolean {
  return event.target instanceof HTMLElement && event.target.closest(SELECTION_CHECKBOX_ROLE_SELECTOR) !== null;
}

/** Maps the modifier keys held during a click to the kind of selection the user intends. */
export function getSelectionIntent(event: ReactMouseEvent): SelectionIntent {
  if (event.shiftKey) {
    return SelectionIntent.Range;
  }
  if (event.metaKey || event.ctrlKey) {
    return SelectionIntent.Toggle;
  }
  return SelectionIntent.Replace;
}

export function isOuterRowPresent(entries: LayerEntry[], sortableId: string): boolean {
  return entries.some((entry) => getSortableId(entry) === sortableId);
}

/** The uids of every outer row between the anchor and target (inclusive), flattened across groups. */
export function computeRangeUids(entries: LayerEntry[], anchorSortableId: string, targetSortableId: string): string[] {
  const anchorIndex = entries.findIndex((entry) => getSortableId(entry) === anchorSortableId);
  const targetIndex = entries.findIndex((entry) => getSortableId(entry) === targetSortableId);
  if (anchorIndex < 0 || targetIndex < 0) {
    return [];
  }
  const startIndex = Math.min(anchorIndex, targetIndex);
  const endIndex = Math.max(anchorIndex, targetIndex);
  return entries.slice(startIndex, endIndex + 1).flatMap(getEntryUids);
}

/** The rows still rendered while a drag is in flight: the dragged row itself stays as the drop placeholder, the rest of the dragged selection is plucked out. */
export function getVisibleEntriesDuringDrag(entries: LayerEntry[], activeId: string, draggedSortableIds: readonly string[]): LayerEntry[] {
  const draggedIdSet = new Set(draggedSortableIds);
  return entries.filter((entry) => {
    const sortableId = getSortableId(entry);
    return sortableId === activeId || !draggedIdSet.has(sortableId);
  });
}

/** Commits the drop to the canvas. Returns whether the z-order actually changed. */
export function commitDragEnd(event: DragEndEvent, entries: LayerEntry[], draggedSortableIds: readonly string[]): boolean {
  const {active, over} = event;
  if (!over) {
    return false;
  }
  const activeId = String(active.id);
  const overId = String(over.id);

  const page = getCurrentPage();
  if (!page) {
    return false;
  }

  const outerIds = new Set(entries.map(getSortableId));
  if (outerIds.has(activeId) && outerIds.has(overId)) {
    return reorderOuterRows({activeId, overId, entries, draggedSortableIds, page});
  }
  if (activeId === overId) {
    return false;
  }
  const group = entries.find((e): e is LayerGroupEntry => e.layerType === LayerType.Group && e.memberUids.includes(activeId));
  if (group?.memberUids.includes(overId)) {
    return reorderGroupMember(activeId, overId, group, page);
  }
  return false;
}

/** Drops the dragged block (in its intrinsic top-to-bottom order) at the position the active row landed on, even when that position is unchanged — a multi-drag gathers scattered rows around the drop point. */
function reorderOuterRows({activeId, overId, entries, draggedSortableIds, page}: ReorderOuterRowsParams): boolean {
  const draggedIds = draggedSortableIds.length > 0 ? draggedSortableIds : [activeId];
  const draggedIdSet = new Set(draggedIds);
  const visibleEntries = getVisibleEntriesDuringDrag(entries, activeId, draggedIds);
  const sourceIndex = visibleEntries.findIndex((entry) => getSortableId(entry) === activeId);
  const destinationIndex = visibleEntries.findIndex((entry) => getSortableId(entry) === overId);
  if (sourceIndex < 0 || destinationIndex < 0) {
    return false;
  }
  const reorderedEntries = arrayMove(visibleEntries, sourceIndex, destinationIndex);
  const draggedBlock = entries.filter((entry) => draggedIdSet.has(getSortableId(entry)));
  const finalEntries = reorderedEntries.flatMap((entry) => (getSortableId(entry) === activeId ? draggedBlock : [entry]));
  return page.items.setPmwZOrder(finalEntries.flatMap(getEntryUids).reverse());
}

function reorderGroupMember(activeId: string, overId: string, entry: LayerGroupEntry, page: Page): boolean {
  const destIdx = entry.memberUids.indexOf(overId);
  const topPmwZ = page.items.getItemIdsInOrder().indexOf(entry.memberUids[0]);
  if (destIdx < 0 || topPmwZ < 0) {
    return false;
  }
  const item = page.items.getItem(activeId);
  if (!item) {
    return false;
  }
  return page.items.moveGroupedItemToPmwZIndex(item, topPmwZ - destIdx);
}
